import { storeToRefs } from 'pinia'
import { defineComponent } from 'vue'
import type {
  RouteLocationNormalized,
  RouteLocationNormalizedLoaded,
  RouteLocationRaw,
  RouteRecordRaw
} from 'vue-router'
import { createRouter, createWebHistory } from 'vue-router'

import { attendanceRoutes } from '@/modules/attendance/routes'
import { brandsRoutes } from '@/modules/brands/routes.ts'
import { catalogRoutes } from '@/modules/catalog/routes'
import { dailyViewRoutes } from '@/modules/daily-view/routes'
import { hardwareStoreRoutes } from '@/modules/hardware-store/routes'
import { useLayoutStore } from '@/modules/layout/store/layout.ts'
import { legalEntitiesRoutes } from '@/modules/legal-entity/routes'
import { locationsRoutes } from '@/modules/locations/routes'
import { useLocationsStore } from '@/modules/locations/store/locations.ts'
import { loginRoutes } from '@/modules/log-in/routes.ts'
import { newOrganizationRoutes } from '@/modules/new-organization/routes'
import { onboardingRoutes } from '@/modules/onboarding/routes.ts'
import { promotionsRoutes } from '@/modules/promotions/routes'
import { reportsRoutes } from '@/modules/reports/routes'
import { schedulesRoutes } from '@/modules/schedules/routes'
import { setupRoutes } from '@/modules/setup/routes'
import { shopRoutes } from '@/modules/shop/routes'
import { signUpRoutes } from '@/modules/sign-up/routes.ts'
import { subscriptionRoutes } from '@/modules/subscription/routes'
import { tabsReportRoutes } from '@/modules/tabs-report/routes'
import pinia from '@/store'
import { useAuthStore } from '@/store/auth.js'
import { useConfigStore } from '@/store/config.ts'
import type { LocationType } from '@/types'

declare module 'vue-router' {
  interface RouteMeta {
    locationTypes?: LocationType[]
    permissions?: string[]
  }
}

const PageNotFound = defineComponent({
  created: function () {
    window.location.href = '/'
  }
})

const routes = [
  {
    path: '/',
    component: () => import('./views/Dashboard.vue'),
    redirect: {
      name: 'home'
    },
    children: [
      ...dailyViewRoutes,
      {
        name: 'config',
        path: '/config',
        component: () => import('./views/Config.vue'),
        meta: {
          locationTypes: ['location', 'singleLocation']
        }
      },
      ...subscriptionRoutes,
      {
        name: 'printers',
        path: '/printers',
        component: () => import('./views/Printers.vue'),
        meta: {
          locationTypes: ['location', 'singleLocation']
        }
      },
      ...catalogRoutes,
      {
        name: 'delivery',
        path: '/delivery',
        component: () => import('@/views/Delivery.vue'),
        meta: {
          locationTypes: ['location', 'singleLocation']
        }
      },
      {
        name: 'ordersLimit',
        path: '/orders-limit',
        component: () => import('@/views/OrderingLimits.vue'),
        meta: {
          locationTypes: ['location', 'singleLocation']
        }
      },
      {
        name: 'floorPlan',
        path: '/floor-plan',
        component: () => import('@/modules/floorplan/views/Floorplan.vue'),
        meta: {
          locationTypes: ['location', 'singleLocation']
        }
      },
      {
        name: 'devices',
        path: '/devices',
        component: () => import('@/views/Devices.vue'),
        meta: {
          locationTypes: ['location', 'singleLocation']
        }
      },
      {
        name: 'integrations',
        path: '/integrations',
        component: () => import('@/views/Integrations.vue'),
        meta: {
          locationTypes: ['location', 'singleLocation']
        }
      },
      {
        name: 'integrationValidate',
        path: '/integrations/:id/validate',
        component: () => import('@/modules/integrations/IntegrationValidator.vue'),
        meta: {
          locationTypes: ['location', 'singleLocation']
        }
      },
      {
        name: 'employees',
        path: '/employees',
        component: () => import('@/modules/employees/views/Employees.vue'),
        meta: {
          locationTypes: ['location', 'singleLocation']
        }
      },
      {
        name: 'posConfig',
        path: '/pos-config',
        component: () => import('@/views/PosConfig.vue'),
        meta: {
          locationTypes: ['location', 'singleLocation']
        }
      },
      {
        name: 'fastModeConfig',
        path: '/fast-mode-config',
        component: () => import('@/components/FastModeConfig.vue'),
        meta: {
          locationTypes: ['location', 'singleLocation']
        }
      },
      ...legalEntitiesRoutes,
      {
        name: 'cardRequest',
        path: '/legal-entities/:legalEntityId/card-request',
        component: () => import('@/components/CardRequest.vue'),
        meta: {
          locationTypes: ['organization', 'singleLocation', 'location']
        }
      },
      {
        name: 'invoice',
        path: '/invoices/:id',
        component: () => import('@/modules/billing/views/Invoice.vue'),
        meta: {
          locationTypes: ['organization', 'singleLocation', 'location']
        }
      },
      ...brandsRoutes,
      ...locationsRoutes,
      {
        name: 'customers',
        path: '/customers',
        component: () => import('@/views/Customers.vue'),
        meta: {
          locationTypes: ['organization', 'singleLocation']
        }
      },
      {
        name: 'customer',
        path: '/customers/:customerId',
        component: () => import('@/views/Customer.vue'),
        meta: {
          locationTypes: ['organization', 'singleLocation']
        }
      },
      {
        name: 'users',
        path: '/users',
        component: () => import('@/views/Users.vue'),
        meta: {
          locationTypes: ['organization', 'singleLocation']
        }
      },
      ...promotionsRoutes,
      {
        name: 'movementsList',
        path: '/movements',
        component: () => import('@/modules/billing/views/Movements.vue'),
        meta: {
          locationTypes: ['location', 'singleLocation']
        }
      },
      {
        name: 'payoutsList',
        path: '/payouts',
        component: () => import('@/modules/billing/views/Payouts.vue'),
        meta: {
          locationTypes: ['location', 'singleLocation']
        }
      },
      {
        name: 'payout',
        path: '/payouts/:id',
        component: () => import('@/modules/billing/views/Payout.vue'),
        meta: {
          locationTypes: ['location', 'singleLocation']
        }
      },
      ...shopRoutes,
      {
        name: 'QR',
        path: '/location/QR',
        component: () => import('@/modules/qr/views/QR.vue'),
        meta: {
          locationTypes: ['location', 'singleLocation']
        }
      },
      {
        name: 'organizationBookings',
        path: '/organization/bookings',
        component: () => import('@/modules/bookings/view/OrganizationConfig.vue'),
        meta: {
          locationTypes: ['organization']
        }
      },
      {
        name: 'singleLocationBookings',
        path: '/scripts',
        component: () => import('@/modules/bookings/view/OrganizationConfig.vue'),
        meta: {
          locationTypes: ['singleLocation']
        }
      },
      ...schedulesRoutes,
      {
        name: 'temporalClosures',
        path: '/temporal-closures',
        component: () => import('@/modules/temporal-closures/views/TemporalClosures.vue'),
        meta: {
          locationTypes: ['location', 'singleLocation']
        }
      },
      ...hardwareStoreRoutes,
      {
        path: '/location/bookings',
        children: [
          {
            name: 'bookingsConfig',
            path: 'config',
            component: () => import('@/components/BookingsConfig.vue'),
            meta: {
              locationTypes: ['location', 'singleLocation']
            }
          },
          {
            name: 'bookingsManager',
            path: 'manager',
            component: () => import('@/modules/bookings/view/Manager.vue'),
            meta: {
              locationTypes: ['location', 'singleLocation']
            }
          },
          {
            name: 'bookingsCommunications',
            path: 'communications',
            component: () => import('@/modules/bookings/view/Communications.vue'),
            meta: {
              locationTypes: ['location', 'singleLocation']
            }
          },
          {
            name: 'bookingsAvailability',
            path: 'availability',
            component: () => import('@/modules/bookings/view/Availability.vue'),
            meta: {
              locationTypes: ['location', 'singleLocation']
            }
          },
          {
            name: 'bookingsPolicy',
            path: 'policies',
            component: () => import('@/modules/bookings/view/Policies.vue'),
            meta: {
              locationTypes: ['location', 'singleLocation']
            }
          },
          {
            name: 'bookingsShifts',
            path: 'shifts',
            component: () => import('@/modules/bookings/view/Shifts.vue'),
            meta: {
              locationTypes: ['location', 'singleLocation']
            }
          }
        ]
      },
      {
        name: 'lastProducts',
        path: '/last-products',
        component: () => import('@/modules/last-products/views/LastProducts.vue'),
        meta: {
          locationTypes: ['location', 'singleLocation']
        }
      },
      ...attendanceRoutes,
      ...tabsReportRoutes,
      ...reportsRoutes
    ]
  },
  ...setupRoutes,
  ...newOrganizationRoutes,
  ...loginRoutes,
  {
    name: 'supportLogin',
    path: '/support/login',
    component: () => import('@/components/SupportLogin.vue')
  },
  {
    name: 'redirectLogin',
    path: '/redirect/login',
    component: () => import('@/components/RedirectLogin.vue')
  },
  ...signUpRoutes,
  ...onboardingRoutes,
  {
    name: 'newPassword',
    path: '/new-password/:recoverId?/:token?',
    component: () => import('./components/NewPassword.vue')
  },
  {
    name: 'rating',
    path: '/rating/:tabId/vote/:rate',
    component: () => import('@/components/Rating.vue')
  },
  {
    name: 'externalOAuth',
    path: '/external-oauth/:integration',
    component: () => import('@/components/ExternalOAuth.vue')
  },
  { path: '/:notvalid(.*)', component: PageNotFound }
] as RouteRecordRaw[]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, _from, savedPosition) {
    return new Promise(resolve => {
      if (savedPosition) {
        resolve(savedPosition)
      } else if (to.hash) {
        setTimeout(() => {
          const element = document.querySelector(to.hash)
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' })
            resolve({ el: to.hash })
          } else {
            resolve({ top: 0 })
          }
        }, 250)
      } else {
        resolve({ top: 0 })
      }
    })
  }
})

const locationTypeToHomeMap: Record<LocationType, RouteLocationRaw> = {
  organization: { name: 'locations', force: true },
  location: { name: 'home', force: true },
  singleLocation: { name: 'home', force: true }
}

function getLocationType(): LocationType | void {
  const locationsStore = useLocationsStore(pinia)
  const { isSingleLocation, isLocationMode } = storeToRefs(locationsStore)

  if (isLocationMode.value) {
    return 'location'
  } else if (isSingleLocation.value) {
    return 'singleLocation'
  } else if (!isLocationMode.value && !isSingleLocation.value) {
    return 'organization'
  }

  return
}

function getUserPermissions(): string[] {
  const authStore = useAuthStore()
  const { permissions } = storeToRefs(authStore)

  return Object.keys(permissions.value || {})
}

router.beforeEach(
  (
    to: RouteLocationNormalized,
    from: RouteLocationNormalizedLoaded
  ): boolean | RouteLocationRaw => {
    const auth = useAuthStore(pinia)

    const needsAuth = ![
      'login',
      'signup',
      'signupOld',
      'supportLogin',
      'redirectLogin',
      'newPassword',
      'rating',
      'createUser'
    ].includes(to.name as string)
    const isAuthenticated = auth.isAuthenticated
    if (needsAuth && !isAuthenticated) {
      return {
        name: 'login',
        query: { redirect: to.name ? to.name.toString() : '' },
        force: true
      }
    }

    const configStore = useConfigStore()
    const { billingStatus } = storeToRefs(configStore)
    const authStore = useAuthStore()
    const { user } = storeToRefs(authStore)

    const installationRoutes = [
      'installation',
      'hardwareStore',
      'hardwareStoreHome',
      'hardwareStoreOrders',
      'hardwareStoreCheckout',
      'setup'
    ]
    if (
      !user.value?.isSupport &&
      billingStatus.value?.installation &&
      !installationRoutes.includes(to.name as string)
    ) {
      return {
        name: 'installation',
        force: true
      }
    }

    const locationType = getLocationType()
    if (
      locationType &&
      to.meta.locationTypes &&
      to.meta.locationTypes.length > 0 &&
      !to.meta.locationTypes.includes(locationType) &&
      to.name !== 'hardwareStore' &&
      to.name !== 'hardwareStoreHome'
    ) {
      return locationTypeToHomeMap[locationType]
    }

    const layoutStore = useLayoutStore()
    const { showPermissionsErrorOnNextRoute } = storeToRefs(layoutStore)

    const permissions = getUserPermissions()
    if (
      permissions &&
      to.meta.permissions &&
      to.meta.permissions.length > 0 &&
      !permissions.some(permission => to.meta.permissions?.includes(permission)) &&
      to.name !== 'hardwareStore' &&
      to.name !== 'hardwareStoreHome'
    ) {
      if (
        !from.meta.permissions ||
        (from.meta.permissions.length > 0 &&
          permissions.some(permission => from.meta.permissions?.includes(permission)))
      ) {
        showPermissionsErrorOnNextRoute.value = true

        return from
      } else if (locationType) return locationTypeToHomeMap[locationType]
    }

    return true
  }
)

export default router
